<script>
export default {
  name: 'ReturnHistory'
}
</script>

<script setup>
import { ref, reactive, onMounted } from 'vue';

import Pagination from '@parts/Pagination.vue';
import ReturnNavigation from '@parts/ReturnNavigation.vue';
import ReturnGoodsList from '@parts/ReturnGoodsList.vue';
import { dateUtil } from '@functions/dateUtil.js';
import DatePicker from '@parts/DatePicker.vue';
import axios from 'axios';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import { url } from '@/config/urls';
import { message } from '@functions/message.js';

// 外部定義メソッド
const { comparisonDateString, checkDateFormat, examValidDate } = dateUtil();

// 画面ID
const screenId = ref("ReturnHistory");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// 定数定義
const pageSizeArray = [50, 100, 200];

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  goodsTypesApiUrl: { 
    type: String,
    default: url.api.goodsTypes
  },
  returnHistoryListApiUrl: {
    type: String,
    default: url.api.returnHistory.list
  },
  returnBoxCountApiUrl: {
    type: String,
    default: url.api.returnBox.count
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});

// ユーザー情報
const user = reactive({
  isPermittedReturnRequest: null  // null: 未取得, true: 権限あり, false: 権限なし
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedReturnRequest = response.data.response.isPermittedReturnRequest;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

// 商品一覧取得APIリクエストパラメータ
const condition = ref({
  words: null,
  startDate: null,
  endDate: null,
  goodsTypeCode: null,
  returnStatusList: [],
  size: 50,
  page: 1
});

// フォーム入力値
const formInputs = ref({
  words: '',
  startDate: '',
  endDate: '',
  goodsTypeCode: '',
  returnStatusList: []
});

// エラー
const errors = ref({
  words: '',
  date: '',
  goodsTypeCode: ''
});

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// 検索結果
const goodsList = ref([]);
const totalNum = ref(0);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

// 返品ボックス行数
const returnBoxTotalRecord = ref(0);
// 返品ボックス行数集計
const countReturnBox = () => {
  axios.get(props.returnBoxCountApiUrl)
    .then((response) => {
      returnBoxTotalRecord.value = parseInt(response.data.response.totalNum);
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes
      .filter(item => item.code !== "02" && item.code !== "04");
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品一覧取得処理
const getGoodsList = async() => {
  try {
    const response = await axios.get(
      props.returnHistoryListApiUrl
      , { params:condition.value }
    );
    if (response.data.response.goodsList) {
      goodsList.value = response.data.response.goodsList;
    }
    totalNum.value = parseInt(response.data.response.totalNum);
    pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
    totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
    totalPage.value = totalPage.value ? totalPage.value : 1;
  } catch(error) {
      apiError(error, errorMessages);
  };
}

// 検索キーワード入力欄Enterキー押下時の処理
const onKeyDownEnterWords = (event) => {
  // 検索時フォーカスアウト(仮想キーボードの非表示)
  event.target.blur();

  clickSearchBtn();
}

// ボタンクリック
const clickSearchBtn = async() => {
  handleUpdateLoading(true);
  dateValidation();
  wordsValidation();
  goodsTypesValidation();
  if (Object.values(errors.value).some(value => value !== '')) {
    handleUpdateLoading(false);
    return;
  }
  setCondition();
  await getGoodsList();
  handleUpdateLoading(false);
}

const clickClearBtn = () => {
  formInputs.value.words = '';
  formInputs.value.goodsTypeCode = '';
  formInputs.value.returnStatusList = [];
  // 期間の初期値設定（開始日：一か月前）
  setStartDate();
  formInputs.value.endDate = '';
}

const clickReturnStatus = (status) => {
  if(status === 'all') {
    formInputs.value.returnStatusList = [];
    return;
  }
  if (formInputs.value.returnStatusList.includes(status)) {
    formInputs.value.returnStatusList = formInputs.value.returnStatusList.filter(s => s !== status);
  } else {
    formInputs.value.returnStatusList.push(status);
  }
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  condition.value.page = page;
  getGoodsList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  condition.value.page = 1;
  condition.value.size = selected;
  getGoodsList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// 検索条件のセット
const setCondition = () => {
  condition.value.startDate = formInputs.value.startDate.replaceAll('/', '-') || null;
  condition.value.endDate = formInputs.value.endDate.replaceAll('/', '-') || null;
  condition.value.words = formInputs.value.words || null;
  condition.value.returnStatusList = formInputs.value.returnStatusList;
  condition.value.goodsTypeCode = formInputs.value.goodsTypeCode || null;
  condition.value.size = pagingParameter.value.pageSize || 50;
  condition.value.page = 1;
  pagingParameter.value.pageNum = 1;
}

// 日付バリデーション
const dateValidation = () => {
  errors.value.date = '';
  // 開始日
  if (formInputs.value.startDate) {
    if (!checkDateFormat(formInputs.value.startDate)) {
      errors.value.date = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.startDate)){
      errors.value.date = message.error.validation.date.validity;
    }
  }
  // 終了日
  if (formInputs.value.endDate){
    if (!checkDateFormat(formInputs.value.endDate)) {
      errors.value.date = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.endDate)){
      errors.value.date = message.error.validation.date.validity;
    }
  }
  // 比較
  if (formInputs.value.startDate && formInputs.value.endDate && !errors.value.date) {
    if (comparisonDateString(formInputs.value.startDate, '>', formInputs.value.endDate)) {
      let errorMessage = message.error.validation.date.comparison;
      errorMessage = errorMessage.replace('{end}', '終了日');
      errorMessage = errorMessage.replace('{start}', '開始日');
      errors.value.date = errorMessage;
    }
  }
}

// 検索キーワードバリデーション
const wordsValidation = () => {
  const maxLength = 255;
  errors.value.words = '';
  if (formInputs.value.words.length > maxLength) {
    let errorMessage = message.error.validation.string.length;
    errorMessage = errorMessage.replace('{max}', maxLength);
    errors.value.words = errorMessage;
  }
}

// 商品分類バリデーション
const goodsTypesValidation = () => {
  errors.value.goodsTypeCode = '';
  if (!formInputs.value.goodsTypeCode) { return; }
  if (!goodsTypes.value.some(goodsType => goodsType.code === formInputs.value.goodsTypeCode)) {
    errors.value.goodsTypeCode = message.error.not_found;
  }
}

// 期間の初期値設定
const setStartDate = () => {
  const today = new Date();
  
  // 開始日：一か月前
  const startDate = new Date(today);
  startDate.setMonth(today.getMonth() - 1);
  const startYear = startDate.getFullYear();
  const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
  const startDay = String(startDate.getDate()).padStart(2, '0');
  const formatStartDate = `${startYear}/${startMonth}/${startDay}`;
  
  formInputs.value.startDate = formatStartDate;
}

// 初期処理
onMounted(async () => {
  await getUserInfo();
  if (!user.isPermittedReturnRequest) {
    return;
  }

  // 期間の初期値設定（開始日：一か月前）
  setStartDate();

  countReturnBox();
  getGoodsTypes();

  // デフォルトで当日分履歴を表示
  setCondition();
  getGoodsList();
});


</script>

<template>

  <div id="contents1" class="bg3" v-if="user.isPermittedReturnRequest">
    <ReturnNavigation
      :screenId = "screenId"
      :returnBoxTotalRecord = returnBoxTotalRecord
    ></ReturnNavigation>

    <h2 class="com_title1 com_mgb5">返品履歴</h2>

    <div class="com_result1">
      <div class="com_inner2">
      <!-- searchForm -->
        <div class="datepicker-group1">
          <div>
            <label>返品依頼日付</label>
            <div>
              <DatePicker
                v-model="formInputs.startDate"
                identifier="js-datepicker1" ref="startDate"
                :class="{ error: errors.date }"
              ></DatePicker>
            </div>
            <span>〜</span>
            <div>
              <DatePicker
                v-model="formInputs.endDate"
                identifier="js-datepicker2" ref="endDate"
                :class="{ error: errors.date }"
              ></DatePicker>
            </div>
          </div>
        </div>
        <div class="com_validate_msg1 st1 error" v-if="errors.date">{{ errors.date }}</div>
        <div class="group14">
          <div>
            <input type="text" placeholder="メーカー名、商品名、商品コード、GTIN"
                class="form-control com_form1" v-model="formInputs.words"
                :class="{ error: errors.words }"
                @keydown.enter="onKeyDownEnterWords" enterkeyhint="search">
            <div class="com_validate_msg1 error" v-if="errors.words">{{ errors.words }}</div>
          </div>
        </div>
        <div class="group11">
          <select class="form-select com_form1" v-model="formInputs.goodsTypeCode" :class="{ error: errors.goodsTypeCode }">
            <option value="" selected>商品分類:すべて</option>
            <option v-for="goodsType in goodsTypes" :key="goodsType.code" :value="goodsType.code">
              {{ goodsType.name }}
            </option>
          </select>
          <button type="button" class="btn com_btn1" @click.prevent="clickSearchBtn">検索</button>
          <button type="button" class="btn com_btn2" @click.prevent="clickClearBtn">クリア</button>
        </div>
        <div class="com_filters1">
          <span class="label1">依頼状況</span>
          <div class="btns1">
            <a href="#" class="btn1" :class="{ selected: formInputs.returnStatusList.length === 0 }" @click.prevent="clickReturnStatus('all')"><span>すべて</span></a>
            <a href="#" class="btn1" :class="{ selected: formInputs.returnStatusList.includes('01') }" @click.prevent="clickReturnStatus('01')"><span>依頼中</span></a>
            <a href="#" class="btn1" :class="{ selected: formInputs.returnStatusList.includes('02') }" @click.prevent="clickReturnStatus('02')"><span>確認中</span></a>
            <a href="#" class="btn1" :class="{ selected: formInputs.returnStatusList.includes('03') }" @click.prevent="clickReturnStatus('03')"><span>返品：◯</span></a>
            <!-- TODO 返品△は当面非表示 -->
            <!--<a href="#" class="btn1" :class="{ selected: formInputs.returnStatusList.includes('05') }" @click.prevent="clickReturnStatus('05')"><span>返品：△</span></a>-->
            <a href="#" class="btn1" :class="{ selected: formInputs.returnStatusList.includes('04') }" @click.prevent="clickReturnStatus('04')"><span>返品：✕</span></a>
          </div>
        </div>
      </div>
    </div>
    <div class="com_inner5 com_mgt30 clearfix">
      <template v-if="goodsList.length === 0">
        <p class="com_fs16 text-center">検索項目を入力してください。</p>
      </template>
      <template v-else>
        <p class="com_fs16 text-center order-results-text">検索結果は以下の通りです</p>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

        <ReturnGoodsList
          :screenId = "screenId"
          :goodsList = goodsList
          :goodsTypes = goodsTypes
          @updateLoading="handleUpdateLoading"
          @countReturnBox="countReturnBox"
          @apiError="handleApiError"
        ></ReturnGoodsList>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

      </template>
    </div>
  </div>
    
  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>